import { PROVINCE_CODE_MAP } from './constants'

// PROVINCE_CODE_MAP comes from an untyped .js module; treat it as a string lookup.
const provinceNameToCode = PROVINCE_CODE_MAP as Record<string, string | undefined>

interface ProvinceCodeDisplayArgs {
  /** Two-letter province/state code as stored on the address (preferred). */
  provinceCode?: string | null
  /** Full province/state name, mapped to a code via PROVINCE_CODE_MAP as a fallback. */
  province?: string | null
  /** Locale flag (e.g. UK) — when true the province is omitted entirely. */
  skipProvinceInAddress?: boolean
}

/**
 * Resolve the province/state code to show on an address line.
 *
 * - Returns '' when the locale doesn't use a province (`skipProvinceInAddress`),
 *   so locales like the UK render no province segment.
 * - Otherwise prefers the stored `provinceCode`, falling back to mapping the full
 *   `province` name through PROVINCE_CODE_MAP, and finally to '' when neither resolves.
 */
export function getProvinceCodeDisplay({
  provinceCode,
  province,
  skipProvinceInAddress,
}: ProvinceCodeDisplayArgs): string {
  if (skipProvinceInAddress) return ''
  // Empty strings fall through, so explicit truthiness — not `??` — is intended here.
  if (provinceCode) return provinceCode
  const mappedCode = province ? provinceNameToCode[province] : undefined
  return mappedCode ?? ''
}
